<template>
    <VPopper class="task-filter" offset-distance="6" placement="bottom-start" z-index="10000" v-bind="$attrs">
        <VButton
            class="button--secondary button--default button--center button--vcenter task-filter__control"
            :class="{ 'task-filter__control--active': filtersCount > 0 }"
        >
            <FilterSvg></FilterSvg>

            <span :counter="filtersCount">
                {{ t('filter') }}
            </span>
        </VButton>

        <template #content="{ close }">
            <VGridContainer class="filter-dropdown">
                <VGridRow class="grid--space-between">
                    <VGridColumn class="grid--center">
                        <h5 class="filter-dropdown__header">
                            {{ t('filters') }}
                        </h5>
                    </VGridColumn>

                    <VGridColumn>
                        <VGridRow>
                            <VButton class="button--link button--vcenter button--margin-right" @click="clear">
                                {{ t('clear') }}
                            </VButton>

                            <VButton
                                class="button--rounded button--transparent button--vcenter button--margin-left"
                                @click="close"
                            >
                                <TimesSvg></TimesSvg>
                            </VButton>
                        </VGridRow>
                    </VGridColumn>
                </VGridRow>

                <VGridRow>
                    <VGridContainer>
                        <template v-for="(filter, index) in filters" :key="index">
                            <VGridRow class="grid--padding-top-100"> {{ filter.title }}: </VGridRow>

                            <VGridRow class="grid--padding-top-25">
                                <component v-on="filter.on" v-bind="filter.attrs" :is="filter.component"></component>
                            </VGridRow>
                        </template>
                    </VGridContainer>
                </VGridRow>

                <template v-for="(filter, index) in checkboxFilters" :key="`chk-${index}`">
                    <VGridRow class="grid--padding-top-100">
                        <component v-on="filter.on" :is="filter.component" v-bind="filter.attrs"></component>
                    </VGridRow>
                </template>

                <VGridRow class="grid--padding-top-100" v-show="options.length">
                    <VButtonDropdown :options="options" placement="bottom-start">
                        <template #button>
                            <VButton class="button--secondary button--transparent">
                                <PlusSvg></PlusSvg>

                                {{ t('add-filter') }}
                            </VButton>
                        </template>
                    </VButtonDropdown>
                </VGridRow>
            </VGridContainer>
        </template>
    </VPopper>
</template>

<script setup lang="ts">
// Svg
import PlusSvg from '@/assets/plus.svg';
import TimesSvg from '@/assets/times.svg';
import FilterSvg from '@/assets/filter.svg';

// Components
import VPopper from 'vue3-popper';
import VButton from './VButton.vue';
import VButtonDropdown from './VButtonDropdown.vue';
import VGridRow from './VGridRow.vue';
import VGridColumn from './VGridColumn.vue';
import VGridContainer from './VGridContainer.vue';

// Other
import { useI18n } from 'vue-i18n';
import { computed, defineProps, PropType, ref, defineEmits, watch } from 'vue';
import { IDropdownOption } from '@/core/Values/IDropdownOption';
import { IFilterPopupContext, IFilterPopupOption, InitFilterPopupOption } from '@/core/Values/IFilterPopup';

const { t } = useI18n();
const emit = defineEmits(['update:modelValue']);
const props = defineProps({
    options: { type: Object as PropType<InitFilterPopupOption[]>, required: true },
    modelValue: { type: Object, required: true },
});

let options = ref<IDropdownOption[]>([]);
let filters = ref<IFilterPopupOption[]>([]);
let checkboxFilters = ref<IFilterPopupOption[]>([]);
let context: IFilterPopupContext = {
    t,
    value: {},
    submit,
    addFilter,
    removeFilter,
    addOption,
    removeOption,
};

const filtersCount = computed(() => Object.values(props.modelValue).filter((value) => value).length);

watch(() => props.options, initFilters);
watch(() => props.modelValue, initFilters, { immediate: true });

function clear() {
    emit('update:modelValue', {}); // Should call initFilters thought watcher.
}

function submit(filter: Record<string, unknown>): void {
    context.value = { ...props.modelValue, ...filter };
    emit('update:modelValue', context.value);
}

function addFilter(filter: IFilterPopupOption): void {
    if (filter.isCheckbox) {
        if (!checkboxFilters.value.includes(filter)) {
            checkboxFilters.value.push(filter);
        }
        return;
    }

    const index = filters.value.indexOf(filter);

    if (index === -1) {
        filters.value.push(filter);
    }
}

function removeFilter(filter: IFilterPopupOption): void {
    const index = filters.value.indexOf(filter);

    if (index !== -1) {
        filters.value.splice(index, 1);
    }
}

function addOption(option: IDropdownOption): void {
    const potentialCheckbox = option as IFilterPopupOption;

    if (potentialCheckbox.isCheckbox) {
        if (!checkboxFilters.value.includes(potentialCheckbox)) {
            checkboxFilters.value.push(potentialCheckbox);
        }
        return;
    }

    const index = options.value.indexOf(option);

    if (index === -1) {
        options.value.push(option);
    }
}

function removeOption(option: IDropdownOption): void {
    const index = options.value.indexOf(option);

    if (index !== -1) {
        options.value.splice(index, 1);
    }
}

function initFilters() {
    if (props.modelValue === context.value) {
        return;
    }

    context.value = structuredClone(props.modelValue);
    options.value = [];
    filters.value = [];
    checkboxFilters.value = [];
    props.options.forEach((option) => option(context));
}
</script>

<style lang="scss">
.filter-dropdown {
    width: 18rem !important;

    @media (min-width: #{$breakpoint-tablet + 1}) {
        width: 22.25rem !important;
    }

    &__header {
        margin: 0;
        padding: 0;

        @include h5();
    }

    &__row {
        padding: 0.25rem 0;

        &--separate {
            border-top: 0.0625rem solid var(--background-tertiary);
        }
    }
}

.task-filter {
    &__control {
        line-height: 1.1875rem;

        &--active {
            color: var(--brand-green);

            span::after {
                content: attr(counter);
                flex-shrink: 0;
                margin-left: 0.25rem;
                padding: 0.1875rem 0.375rem;

                @include caption-tertiary(false);
                font-size: 0.6875rem;
                border-radius: 0.75rem;
                color: var(--text-white-primary);
                background: var(--background-color);
                --background-color: var(--brand-green);
            }
        }
    }
}
</style>
